import Chip from '@mui/material/Chip';
import { MemreButton, MemreFlexBox, MemreText } from 'components/core';
import { Modal } from 'components/Modal';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useDeletePartnerUser } from 'hooks/useDeletePartnerUser';
import { usePartnerSummaryCounts } from 'hooks/usePartnerSummaryCounts';
import { usePartnerUsers } from 'hooks/usePartnerUsers';
import { AddUserModal } from 'pages/admin-dashboard/people/AddUserModal';
import { CsvUploadModal } from 'pages/admin-dashboard/people/CsvUploadModal';
import { usePeopleStore } from 'pages/admin-dashboard/people/store';
import { useState } from 'react';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

export const Header = () => {
  const { data: partnerSummaryCounts } = usePartnerSummaryCounts();
  const userCount = partnerSummaryCounts?.usersCount;
  const plusDepartmentsCount =
    (partnerSummaryCounts?.usersCountIncludingDepartments ?? 0) - (partnerSummaryCounts?.usersCount ?? 0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);

  const user = useCurrentUser();

  // Grab rowSelection from store
  const { rowSelection, setRowSelection } = usePeopleStore();
  const selectedCount = Object.keys(rowSelection).length;

  // For looking up selected users
  const { data: response, refetch } = usePartnerUsers();
  const [deletePartnerUser] = useDeletePartnerUser();
  const people = normalizeJSONAPIResponse(response?.data);

  const handleBulkDelete = async () => {
    if (!selectedCount) return;

    if (!window.confirm('Are you sure you want to remove selected users?')) {
      return;
    }

    try {
      // By default, each row's ID is its array index in React Table,
      // so rowSelection keys are numeric indices.
      const selectedRowIndices = Object.keys(rowSelection).map((k) => Number(k));
      const selectedUsers = selectedRowIndices.map((idx) => people[idx]).filter(Boolean);

      await Promise.all(
        selectedUsers.map((u) =>
          deletePartnerUser({
            partnerId: user?.primaryPartner?.id,
            userId: u.id,
          })
        )
      );

      setRowSelection({});
      refetch();
    } catch (error) {
      console.error('Error in bulk delete:', error);
    }
  };

  return (
    <>
      <MemreFlexBox gap={4} direction="row" justify="space-between" align="center">
        <div>
          <MemreText variant="h3">People</MemreText>
          <MemreFlexBox gap={1} direction="row" align="center">
            <MemreText variant="subtitle2">{userCount} active accounts</MemreText>
            <Chip label={`${plusDepartmentsCount} in departments`} size="small" color="secondary" variant="outlined" />
          </MemreFlexBox>
        </div>

        {/* Right Side Buttons */}
        <MemreFlexBox gap={2} direction="row" align="center">
          {selectedCount > 0 && (
            <MemreButton
              text={`Remove ${selectedCount} user${selectedCount !== 1 ? 's' : ''}`}
              size="small"
              color="error"
              variant="contained"
              onClick={handleBulkDelete}
            />
          )}
          <MemreButton
            text="Add by Email"
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => setIsModalOpen(true)}
          />
          <MemreButton
            text="Add/Remove via CSV"
            size="small"
            color="secondary"
            variant="outlined"
            onClick={() => setIsCsvModalOpen(true)}
          />
        </MemreFlexBox>
      </MemreFlexBox>

      {isModalOpen && <Modal show={true} element={<AddUserModal onClose={() => setIsModalOpen(false)} />} />}
      {isCsvModalOpen && <CsvUploadModal onClose={() => setIsCsvModalOpen(false)} />}
    </>
  );
};
